import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Send, Eye, Trash2, CheckCircle, Clock, AlertCircle, ExternalLink, DollarSign, Download, RefreshCw, CreditCard, Banknote, Building2, MessageSquare } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-zinc-700 text-zinc-300',
  sent: 'bg-blue-500/20 text-blue-400',
  viewed: 'bg-purple-500/20 text-purple-400',
  paid: 'bg-green-500/20 text-green-400',
  overdue: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-zinc-600 text-zinc-400',
};

const paymentStatusColors = {
  unpaid: 'bg-yellow-500/20 text-yellow-400',
  partial: 'bg-orange-500/20 text-orange-400',
  paid: 'bg-green-500/20 text-green-400',
};

export default function AdminInvoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [form, setForm] = useState({
    customer_id: '',
    items: [{ product_id: '', description: '', quantity: 1, unit_price: 0 }],
    shipping: 0,
    payment_terms: 'Due on Receipt',
    due_date: '',
    notes: '',
    internal_notes: '',
    deduct_stock: true,
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
    fetchPaymentMethods();
  }, []);

  // Handle search highlight - auto-open invoice from global search
  useEffect(() => {
    const highlightId = searchParams.get('highlight') || location.state?.highlightId;
    if (!highlightId) return;

    const openInvoice = async (id) => {
      let invoice = invoices.find(i => i.id === id);
      if (!invoice) {
        try {
          const res = await axios.get(`${API}/admin/invoices/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          invoice = res.data;
        } catch { return; }
      }
      if (invoice) {
        setSelectedInvoice(invoice);
        setShowViewModal(true);
      }
      setSearchParams({}, { replace: true });
      window.history.replaceState({}, '');
    };

    openInvoice(highlightId);
  }, [invoices, searchParams, location.state]);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products?limit=50`),
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data.products || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/payment-methods/active`);
      setPaymentMethods(res.data.methods || []);
    } catch {
      setPaymentMethods([
        { id: 'stripe', name: 'Credit Card (Stripe)' },
        { id: 'direct_deposit', name: 'Direct Deposit' },
        { id: 'cash', name: 'Cash' },
      ]);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const validItems = form.items.filter(i => i.description && i.unit_price > 0);
      if (validItems.length === 0) {
        toast.error('Please add at least one item');
        return;
      }
      await axios.post(`${API}/admin/invoices`, {
        ...form,
        items: validItems,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoiceId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleSendReceipt = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoiceId}/send-receipt`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Receipt sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send receipt');
    }
  };

  const handleMarkPaid = async (invoiceId, paymentMethod) => {
    try {
      await axios.post(`${API}/admin/invoices/${invoiceId}/mark-paid`, 
        { payment_method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invoice marked as paid (${paymentMethod})`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update invoice');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) return;
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const resetForm = () => {
    setForm({
      customer_id: '',
      items: [{ product_id: '', description: '', quantity: 1, unit_price: 0 }],
      shipping: 0,
      payment_terms: 'Due on Receipt',
      due_date: '',
      notes: '',
      internal_notes: '',
      deduct_stock: true,
    });
  };

  const addLineItem = () => {
    setForm({ ...form, items: [...form.items, { product_id: '', description: '', quantity: 1, unit_price: 0 }] });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-fill description and price when product selected
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].description = product.name;
        newItems[index].unit_price = product.pricing?.retail || 0;
      }
    }
    
    setForm({ ...form, items: newItems });
  };

  const removeLineItem = (index) => {
    if (form.items.length === 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const getSubtotal = () => {
    return form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const getTax = () => getSubtotal() * 0.10;
  const getTotal = () => getSubtotal() + getTax() + (parseFloat(form.shipping) || 0);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-invoices">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Invoices</h1>
            <p className="text-zinc-400 mt-1">All invoices — online orders and manually created</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
            data-testid="create-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Invoice
          </Button>
        </div>

        {/* Invoice Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-zinc-400 text-sm">Total Invoices</p>
            <p className="text-2xl font-bold text-white">{invoices.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-zinc-400 text-sm">Unpaid</p>
            <p className="text-2xl font-bold text-yellow-400">
              {invoices.filter(i => i.payment_status === 'unpaid' && i.status !== 'cancelled').length}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-zinc-400 text-sm">Paid</p>
            <p className="text-2xl font-bold text-green-400">
              {invoices.filter(i => i.payment_status === 'paid').length}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-zinc-400 text-sm">Total Revenue</p>
            <p className="text-2xl font-bold text-[#78BE20]">
              ${invoices.filter(i => i.payment_status === 'paid').reduce((sum, i) => sum + i.total, 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Invoice List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-zinc-400">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center">
              <p className="text-zinc-400">No invoices yet. Create your first invoice to get started.</p>
            </div>
          ) : (
            invoices.map(invoice => (
              <div 
                key={invoice.id} 
                className="bg-zinc-900 border border-zinc-800 rounded-sm p-4"
                data-testid={`invoice-${invoice.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-white">{invoice.invoice_number}</h3>
                      {invoice.source === 'order' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-medium">Order</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-medium">Invoice</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded uppercase font-medium ${statusColors[invoice.status]}`}>
                        {invoice.source === 'order' ? (invoice.order_status || invoice.status) : invoice.status}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded uppercase font-medium ${paymentStatusColors[invoice.payment_status]}`}>
                        {invoice.payment_status}
                      </span>
                      {invoice.payment_method && invoice.payment_status === 'paid' && (
                        <span className="text-xs text-zinc-400">{invoice.payment_method}</span>
                      )}
                      {invoice.xero_invoice_id && (
                        <span className="text-xs text-[#13B5EA]">Xero</span>
                      )}
                      {invoice.source_ticket_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/tickets/${invoice.source_ticket_id}`)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-2 py-0.5 h-auto text-xs"
                          data-testid={`invoice-ticket-link-${invoice.id}`}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {invoice.source_ticket_id.startsWith('TKT-') ? invoice.source_ticket_id : 'Ticket'}
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                      Customer: <span className="text-white">{invoice.customer_name}</span>
                      <span className="text-zinc-500 ml-2">({invoice.customer_email})</span>
                    </p>
                    <div className="flex gap-6 mt-2 text-sm">
                      <span className="text-zinc-400">
                        Items: <span className="text-white">{invoice.items?.length || 0}</span>
                      </span>
                      <span className="text-zinc-400">
                        Total: <span className="text-[#78BE20] font-bold">${invoice.total?.toFixed(2)}</span>
                      </span>
                      <span className="text-zinc-400">
                        Terms: <span className="text-white">{invoice.payment_terms}</span>
                      </span>
                      <span className="text-zinc-500">
                        {new Date(invoice.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {!invoice.xero_invoice_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#13B5EA] hover:text-[#0fa0d0]"
                        onClick={async () => {
                          try {
                            const endpoint = invoice.source === 'order'
                              ? `${API}/xero/sync-order/${invoice.id}`
                              : `${API}/xero/sync-invoice/${invoice.id}`;
                            await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Synced to Xero');
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to sync to Xero');
                          }
                        }}
                        title="Sync to Xero"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Xero
                      </Button>
                    )}
                    {invoice.xero_invoice_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#13B5EA]/50 hover:text-[#13B5EA]"
                        onClick={async () => {
                          try {
                            const endpoint = invoice.source === 'order'
                              ? `${API}/xero/sync-order/${invoice.id}`
                              : `${API}/xero/sync-invoice/${invoice.id}`;
                            await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Re-synced to Xero');
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to re-sync to Xero');
                          }
                        }}
                        title="Re-sync to Xero"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" /> Xero
                      </Button>
                    )}
                    {invoice.source !== 'order' && invoice.status === 'draft' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-blue-400 hover:text-blue-300"
                        onClick={() => handleSendInvoice(invoice.id)}
                      >
                        <Send className="h-4 w-4 mr-1" /> Send
                      </Button>
                    )}
                    {invoice.source !== 'order' && invoice.status === 'sent' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-blue-400 hover:text-blue-300"
                        onClick={() => handleSendInvoice(invoice.id)}
                        title="Resend Invoice"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" /> Resend
                      </Button>
                    )}
                    {invoice.source !== 'order' && invoice.payment_status === 'paid' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-400 hover:text-green-300"
                        onClick={() => handleSendReceipt(invoice.id)}
                        title="Send paid invoice receipt to customer"
                        data-testid={`send-receipt-${invoice.id}`}
                      >
                        <Send className="h-4 w-4 mr-1" /> Receipt
                      </Button>
                    )}
                    {invoice.source !== 'order' && invoice.stripe_payment_link && invoice.payment_status !== 'paid' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(invoice.stripe_payment_link, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Pay Link
                      </Button>
                    )}
                    {invoice.source !== 'order' && invoice.payment_status !== 'paid' && invoice.status !== 'cancelled' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-400 hover:text-green-300"
                            data-testid={`mark-paid-${invoice.id}`}
                          >
                            <DollarSign className="h-4 w-4 mr-1" /> Mark Paid
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                          {paymentMethods.map(method => (
                            <DropdownMenuItem 
                              key={method.id}
                              onClick={() => handleMarkPaid(invoice.id, method.name)}
                              className="text-zinc-300 hover:text-white focus:text-white cursor-pointer"
                              data-testid={`pay-${method.id}-${invoice.id}`}
                            >
                              <CreditCard className="h-4 w-4 mr-2 text-green-400" /> {method.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {invoice.source !== 'order' && invoice.status === 'draft' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleCancelInvoice(invoice.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Create Invoice Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInvoice} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer *</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.company_name || c.name} ({c.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 7">Net 7</SelectItem>
                      <SelectItem value="Net 14">Net 14</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Line Items</Label>
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-end">
                      <div className="w-48">
                        <Select value={item.product_id || 'custom'} onValueChange={(v) => updateLineItem(idx, 'product_id', v === 'custom' ? '' : v)}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue placeholder="Product (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom Item</SelectItem>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.sku} - {p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <Input
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="Description *"
                        />
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="Qty"
                        />
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="Price $"
                        />
                      </div>
                      <div className="w-24 text-right text-white">
                        ${(item.quantity * item.unit_price).toFixed(2)}
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => removeLineItem(idx)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addLineItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Shipping</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.shipping}
                    onChange={(e) => setForm({ ...form, shipping: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>

              <div className="p-3 bg-zinc-800 rounded space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Subtotal:</span>
                  <span className="text-white">${getSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">GST (10%):</span>
                  <span className="text-white">${getTax().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Shipping:</span>
                  <span className="text-white">${(parseFloat(form.shipping) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-zinc-700 pt-2 mt-2">
                  <span className="text-zinc-400">Total:</span>
                  <span className="text-[#78BE20]">${getTotal().toFixed(2)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox 
                  id="deduct_stock" 
                  checked={form.deduct_stock}
                  onCheckedChange={(checked) => setForm({ ...form, deduct_stock: checked })}
                />
                <Label htmlFor="deduct_stock" className="text-sm text-zinc-400">
                  Deduct stock when invoice is created
                </Label>
              </div>

              <div>
                <Label>Notes (visible to customer)</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>

              <div>
                <Label>Internal Notes (private)</Label>
                <Textarea
                  value={form.internal_notes}
                  onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                  Create Invoice
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Invoice Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                {selectedInvoice?.invoice_number}
                {selectedInvoice?.source_ticket_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowViewModal(false); navigate(`/admin/tickets/${selectedInvoice.source_ticket_id}`); }}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-2 py-1 h-auto text-xs"
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    View Ticket
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Customer:</span>
                    <p className="text-white">{selectedInvoice.customer_name}</p>
                    <p className="text-zinc-400">{selectedInvoice.customer_email}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Status:</span>
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs uppercase font-medium ${statusColors[selectedInvoice.status]}`}>
                        {selectedInvoice.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs uppercase font-medium ${paymentStatusColors[selectedInvoice.payment_status]}`}>
                        {selectedInvoice.payment_status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-500">Created:</span>
                    <p className="text-white">{new Date(selectedInvoice.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Payment Terms:</span>
                    <p className="text-white">{selectedInvoice.payment_terms}</p>
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Line Items</h4>
                  <div className="space-y-2">
                    {selectedInvoice.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-zinc-800 rounded text-sm">
                        <div>
                          <p className="text-white">{item.description}</p>
                          {item.sku && <p className="text-zinc-500 text-xs">{item.sku}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-white">{item.quantity} x ${item.unit_price}</p>
                          <p className="text-zinc-400">${item.total?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Subtotal:</span>
                    <span className="text-white">${selectedInvoice.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">GST:</span>
                    <span className="text-white">${selectedInvoice.tax?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Shipping:</span>
                    <span className="text-white">${selectedInvoice.shipping?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-700">
                    <span className="text-zinc-400">Total:</span>
                    <span className="text-[#78BE20]">${selectedInvoice.total?.toFixed(2)}</span>
                  </div>
                </div>

                {selectedInvoice.notes && (
                  <div className="border-t border-zinc-800 pt-4">
                    <span className="text-zinc-500 text-sm">Notes:</span>
                    <p className="text-zinc-300 text-sm">{selectedInvoice.notes}</p>
                  </div>
                )}

                <div className="border-t border-zinc-800 pt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => downloadPDF(selectedInvoice.id, selectedInvoice.invoice_number)}
                  >
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                  {selectedInvoice.stripe_payment_link && (
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => window.open(selectedInvoice.stripe_payment_link, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" /> Payment Link
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
